import { useMutation, useQueryClient, useQuery } from "@tanstack/react-query";
import { useToast } from "../../../components/toast/useToast";
import KontrolyDokumentService from "./KontrolyDokumentService";
import type { KontrolaDokument, KontrolaDokumentUpdateRequest } from "../generated/models/KontrolaDokument";

// ==========================================
// QUERIES
// ==========================================

export const useKontrolyDokumentyQuery = (typId: number | null) => {
    return useQuery<KontrolaDokument[], Error>({
        queryKey: ["kontrolyDokumenty", typId],
        queryFn: () => KontrolyDokumentService.listDokumenty(typId!),
        enabled: !!typId,
    });
};

// ==========================================
// MUTATIONS
// ==========================================

// BE přijímá pouze "file" + volitelný "nazev" — žádný popis.
export const useUploadDokumentMutation = (typId: number) => {
    const { showToast } = useToast();
    const queryClient = useQueryClient();

    return useMutation<KontrolaDokument, Error, { file: File; nazev?: string }>({
        mutationFn: ({ file, nazev }) =>
            KontrolyDokumentService.uploadDokument(typId, file, nazev),

        onSuccess: () => {
            queryClient.invalidateQueries({ queryKey: ["kontrolyDokumenty", typId] });
            showToast("Dokument byl nahrán.", "success");
        },

        onError: (error) => {
            showToast(error.message || "Nepodařilo se nahrát dokument.", "error");
        },
    });
};

export const useDeleteDokumentMutation = () => {
    const { showToast } = useToast();
    const queryClient = useQueryClient();

    return useMutation<void, Error, { id: number; typId: number }>({
        mutationFn: ({ id }) => KontrolyDokumentService.deleteDokument(id),

        onSuccess: (_data, variables) => {
            queryClient.invalidateQueries({ queryKey: ["kontrolyDokumenty", variables.typId] });
            showToast("Dokument byl smazán.", "success");
        },

        onError: (error) => {
            showToast(error.message || "Nepodařilo se smazat dokument.", "error");
        },
    });
};

export const useUpdateDokumentMutation = () => {
    const { showToast } = useToast();
    const queryClient = useQueryClient();

    return useMutation<KontrolaDokument, Error, { id: number; typId: number; data: KontrolaDokumentUpdateRequest }>({
        mutationFn: ({ id, data }) => KontrolyDokumentService.updateDokumentMetadata(id, data),

        onSuccess: (_data, variables) => {
            queryClient.invalidateQueries({ queryKey: ["kontrolyDokumenty", variables.typId] });
            showToast("Metadata dokumentu byla aktualizována.", "success");
        },

        onError: (error) => {
            showToast(error.message || "Nepodařilo se aktualizovat dokument.", "error");
        },
    });
};

export const useDownloadDokumentMutation = () => {
    const { showToast } = useToast();

    return useMutation<{ blob: Blob; filename: string }, Error, number>({
        mutationFn: (id) => KontrolyDokumentService.downloadDokument(id),

        onError: (error) => {
            showToast(error.message || "Nepodařilo se stáhnout dokument.", "error");
        },
    });
};

// Stažení posudku pro konkrétního uživatele (GET + blob, admin)
export const useDownloadPosudekMutation = () => {
    const { showToast } = useToast();

    return useMutation<{ blob: Blob; filename: string }, Error, number>({
        mutationFn: (userId) => KontrolyDokumentService.downloadPosudek(userId),

        onError: (error) => {
            showToast(error.message || "Nepodařilo se stáhnout posudek.", "error");
        },
    });
};

// Hromadný posudek — POST + blob (nový pattern)
export const useDownloadHromadnyPosudekMutation = () => {
    const { showToast } = useToast();

    return useMutation<{ blob: Blob; filename: string }, Error, number[]>({
        mutationFn: (userIds) => KontrolyDokumentService.downloadHromadnyPosudek(userIds),

        onError: (error) => {
            showToast(error.message || "Nepodařilo se vytvořit hromadný dokument.", "error");
        },
    });
};
